import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { RadioGroupField, TextField } from "$components/rhf-fields";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { InvoiceValidationError } from "$routes/_lib/billing-v2/invoice-source";
import { randomUniqueId } from "$routes/_lib/random-id";
import { updateInvoiceDocument } from "$routes/patient/[:patientId]/_lib/invoices-v2/update-invoice-document";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { differenceInYears, parseISO } from "date-fns";
import {
  ExemptionType,
  PayerType,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import { useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  defineMessages,
  FormattedMessage,
  type MessageDescriptor,
  useIntl,
} from "react-intl";
import type { RxDocument } from "rxdb";
import { object, string } from "yup";

const EXEMPTION_ORDER: ExemptionType[] = [
  ExemptionType.Under_5,
  ExemptionType.Over_70,
  ExemptionType.AncMother,
  ExemptionType.MalariaPatient,
  ExemptionType.PsychiatricPatient,
  ExemptionType.GenderBasedViolence,
  ExemptionType.Prisoner,
  ExemptionType.Other,
];

const EXEMPTION_MESSAGES: Record<ExemptionType, MessageDescriptor> =
  defineMessages({
    [ExemptionType.Under_5]: {
      id: "invoicing.exemptV2.reason.under5",
      defaultMessage: "Under 5 years",
    },
    [ExemptionType.Over_70]: {
      id: "invoicing.exemptV2.reason.over70",
      defaultMessage: "Over 70",
    },
    [ExemptionType.AncMother]: {
      id: "invoicing.exemptV2.reason.ancMother",
      defaultMessage: "ANC mother",
    },
    [ExemptionType.MalariaPatient]: {
      id: "invoicing.exemptV2.reason.malariaPatient",
      defaultMessage: "Malaria patient",
    },
    [ExemptionType.PsychiatricPatient]: {
      id: "invoicing.exemptV2.reason.psychiatricPatient",
      defaultMessage: "Psychiatric patient",
    },
    [ExemptionType.GenderBasedViolence]: {
      id: "invoicing.exemptV2.reason.genderBasedViolence",
      defaultMessage: "Gender-based violence",
    },
    [ExemptionType.Prisoner]: {
      id: "invoicing.exemptV2.reason.prisoner",
      defaultMessage: "Prisoner",
    },
    [ExemptionType.Other]: {
      id: "invoicing.exemptV2.reason.other",
      defaultMessage: "Other",
    },
  });

type FormValues = { exemptionType: ExemptionType | ""; otherText?: string };

const validateAgeExemption = (
  patientDob: string | undefined,
  exemptionType: ExemptionType,
  now: Date,
): void => {
  if (
    exemptionType !== ExemptionType.Under_5 &&
    exemptionType !== ExemptionType.Over_70
  ) {
    return;
  }
  if (!patientDob) {
    throw new InvoiceValidationError(
      "Patient DOB is required to validate exemption",
    );
  }
  const age = differenceInYears(now, parseISO(patientDob));
  if (age >= 5 && exemptionType === ExemptionType.Under_5) {
    throw new InvoiceValidationError(
      "The invoice can not be exempt given that the patient is older than 5",
    );
  }
  if (age < 70 && exemptionType === ExemptionType.Over_70) {
    throw new InvoiceValidationError(
      "The invoice can not be exempt given that the patient is not older than 70",
    );
  }
};

const useStyles = makeStyles((theme) => ({
  warningIcon: { marginRight: theme.spacing(2) },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 600,
    marginLeft: theme.spacing(0.5),
  },
}));

/**
 * V2 exempt flow. Mirrors the legacy `ExemptModal` UX but rxdb-native
 * via `applyPayment`. The full `ExemptionType` enum (extended by
 * PNE-390 — ANC_MOTHER, GENDER_BASED_VIOLENCE, MALARIA_PATIENT,
 * PRISONER, PSYCHIATRIC_PATIENT, OTHER) is offered as radio options;
 * UNDER_5 / OVER_70 are still validated against patient DOB by
 * `applyPayment`, the others pass straight through.
 *
 * When "Other" is selected, a free-text field is required — the entry
 * is persisted into `SyncableTransaction.notes` since the BE
 * `ExemptionType` enum has no per-reason free-text slot.
 */
export const ExemptInvoiceModalV2 = ({
  doc,
  patientDob,
  open,
  onClose,
}: {
  doc: RxDocument<RxInvoice>;
  patientDob?: string;
  open: boolean;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const [busy, setBusy] = useState(false);

  const validationSchema = useMemo(
    () =>
      object({
        exemptionType: string()
          .oneOf(EXEMPTION_ORDER as string[])
          .required(),
        otherText: string().when("exemptionType", {
          is: ExemptionType.Other,
          then: (schema) => schema.trim().min(1).required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [],
  );

  const formMethods = useForm<FormValues>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema) as unknown as ReturnType<
      typeof yupResolver
    >,
    defaultValues: { exemptionType: "", otherText: "" },
  });

  const reasonOptions = useMemo(
    () =>
      EXEMPTION_ORDER.map((value) => ({
        value,
        label: formatMessage(EXEMPTION_MESSAGES[value]),
        "data-testid": `invoicing.exemptV2.reason.${value}`,
      })),
    [formatMessage],
  );

  const watchedReason = useWatch({
    control: formMethods.control,
    name: "exemptionType",
  });

  const onSubmit = async (values: FormValues) => {
    if (!values.exemptionType) return;
    setBusy(true);
    try {
      const now = getCurrentTime();
      validateAgeExemption(patientDob, values.exemptionType, now);
      const timestamp = now.toISOString();
      const transactionId = randomUniqueId();
      await updateInvoiceDocument(doc, {
        actionedByUserId: user.id,
        timestamp,
        update: (invoice) => {
          const current = invoice.toRecord();
          const patient = current.payers.find(
            (payer) => payer.type === PayerType.Patient,
          ) as (typeof current.payers)[number];
          return invoice
            .exempt({
              id: transactionId,
              uuid: null,
              type: TransactionType.Exempt,
              status: TransactionStatus.Success,
              timestamp,
              actionedByUserId: user.id,
              amount: {
                currency: current.balance.currency,
                value: patient.balance.due,
              },
              payerName: values.exemptionType,
              payerType: PayerType.Exempt,
              exemptionType: values.exemptionType,
              ...(values.exemptionType === ExemptionType.Other &&
              values.otherText
                ? { notes: values.otherText.trim() }
                : {}),
            })
            .toRecord();
        },
      });
      onClose();
    } catch (err) {
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          err instanceof Error
            ? err.message
            : formatMessage({
                id: "invoicing.exemptV2.error.generic",
                defaultMessage: "Failed to exempt this invoice",
              }),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContainer
      open={open}
      onClose={onClose}
      data-testid="invoicing.exemptV2.modal"
    >
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.exemptV2.title"
              defaultMessage="Exempt Payment"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <FormProvider {...formMethods}>
        <form
          name="exempt-invoice-v2-form"
          onSubmit={formMethods.handleSubmit(onSubmit)}
        >
          <DialogBody>
            <Typography variant="h6" className={classes.warningMessage}>
              <FormattedMessage
                id="invoicing.exemptV2.message"
                defaultMessage="Select a reason for the exemption"
              />
            </Typography>
            <Box mt={2} ml={2}>
              <RadioGroupField
                name="exemptionType"
                options={reasonOptions}
                required
              />
            </Box>
            {watchedReason === ExemptionType.Other && (
              <Grid container>
                <Grid item xs={12}>
                  <TextField
                    name="otherText"
                    label={formatMessage({
                      id: "invoicing.exemptV2.otherText.label",
                      defaultMessage: "Please specify",
                    })}
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={busy}
                    data-testid="invoicing.exemptV2.otherText"
                  />
                </Grid>
              </Grid>
            )}
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={onClose} disabled={busy}>
              <FormattedMessage
                id="invoicing.exemptV2.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!formMethods.formState.isValid || busy}
              loading={busy}
              data-testid="invoicing.exemptV2.accept"
            >
              <FormattedMessage
                id="invoicing.exemptV2.accept"
                defaultMessage="Exempt"
              />
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};
